import type {
  PendingEnrichmentToken,
  Prayer,
  ProgressEntry,
} from '../types/prayer';

function hex(value: number): string {
  return (value >>> 0).toString(16).padStart(8, '0');
}

/**
 * A deterministic 128-bit browser-safe hash for legacy identity. The full
 * prayer id, timestamp, note, and duplicate ordinal feed four independent
 * mixed lanes. Exact duplicate rows are disambiguated by their oldest-first
 * ordinal, so prepending a new update does not change existing identities.
 */
function hash128(value: string): string {
  let a = 0x243f6a88;
  let b = 0x85a308d3;
  let c = 0x13198a2e;
  let d = 0x03707344;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    a = Math.imul(a ^ code, 0x9e3779b1);
    b = Math.imul(b ^ code, 0x85ebca77);
    c = Math.imul(c ^ code, 0xc2b2ae3d);
    d = Math.imul(d ^ code, 0x27d4eb2f);
    a = (a << 7) | (a >>> 25);
    b = (b << 11) | (b >>> 21);
    c = (c << 15) | (c >>> 17);
    d = (d << 19) | (d >>> 13);
  }
  a ^= b >>> 1;
  b ^= c >>> 1;
  c ^= d >>> 1;
  d ^= a >>> 1;
  return `${hex(a)}${hex(b)}${hex(c)}${hex(d)}`;
}

function legacySignature(entry: ProgressEntry): string {
  return `${entry.date}\u0000${entry.note}`;
}

export function progressEntryIds(
  prayerId: string,
  entries: readonly ProgressEntry[],
): string[] {
  const ids = new Array<string>(entries.length);
  const duplicateOrdinals = new Map<string, number>();
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    const explicit = entry.id?.trim();
    if (explicit) {
      ids[index] = explicit;
      continue;
    }
    const signature = legacySignature(entry);
    const ordinal = duplicateOrdinals.get(signature) ?? 0;
    duplicateOrdinals.set(signature, ordinal + 1);
    ids[index] = `legacy-${hash128(`${prayerId}\u0000${signature}\u0000${ordinal}`)}`;
  }
  return ids;
}

export function materializeProgressEntryIds(prayer: Prayer): Prayer {
  const ids = progressEntryIds(prayer.id, prayer.progressHistory ?? []);
  let changed = false;
  const progressHistory = (prayer.progressHistory ?? []).map((entry, index) => {
    if (entry.id?.trim()) return entry;
    changed = true;
    return { ...entry, id: ids[index] };
  });
  return changed ? { ...prayer, progressHistory } : prayer;
}

export function mintProgressEntryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const entropy = `${Date.now()}\u0000${Math.random()}\u0000${Math.random()}`;
  return `entry-${hash128(entropy)}`;
}

export function enrichmentTarget(
  prayer: Prayer,
): { entryId: string; index: number; token: PendingEnrichmentToken } | null {
  const pending = prayer.pendingEnrichment;
  if (!pending) return null;
  const ids = progressEntryIds(prayer.id, prayer.progressHistory ?? []);
  if (typeof pending === 'string') {
    const index = prayer.progressHistory.findIndex((entry) => entry.date === pending);
    if (index < 0) return null;
    return {
      entryId: ids[index],
      index,
      token: { entryId: ids[index], sourceUpdatedAt: prayer.updatedAt },
    };
  }
  const index = ids.indexOf(pending.entryId);
  if (index < 0) return null;
  return { entryId: pending.entryId, index, token: pending };
}

export function pendingTokenForEntry(
  entryId: string,
  sourceUpdatedAt: string,
): PendingEnrichmentToken {
  return { entryId, sourceUpdatedAt };
}

export function pendingTokensEqual(
  left: Prayer['pendingEnrichment'],
  right: Prayer['pendingEnrichment'],
): boolean {
  if (typeof left === 'string' || typeof right === 'string') return left === right;
  if (!left || !right) return !left && !right;
  return (
    left.entryId === right.entryId &&
    left.sourceUpdatedAt === right.sourceUpdatedAt
  );
}
